import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { Chip, Tooltip, Box } from "@mui/material";
import { GridText } from "src/components/Grid/GridText";
import { useTranslation } from "react-i18next";
import { MrtColumns, extendColumnsByJsonAttributes } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { CompanyGrid } from "src/api/client-api/company/useGetAllOrganization";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useTagContext } from "src/api/client-api/tag";
import { generateTagColumns } from "src/utils/tagColumns";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

export const useCompanyColumns = (data?: CompanyGrid[], isProvider?: boolean): MrtColumns<CompanyGrid> => {
    const { t } = useTranslation();
    const { entitiesJsonAttributes } = useFoodpilotContext();
    const { tagTypes, tagValues } = useTagContext(isProvider ? "supplier" : "organization");

    const columnHelper = createMRTColumnHelper<CompanyGrid>();

    const organizationJsonAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.organization] || [];

    const mrt_columns = [
        columnHelper.accessor((row) => row.id, {
            id: "id",
            header: t("company.id", "ID"),
        }),
        columnHelper.accessor("name", {
            header: t("company.name", "Company name"),
            Cell: (value) => <GridText variant={"themed"} value={value.renderedCellValue} />,
            grow: false,
        }),
        columnHelper.accessor(
            (row) => {
                if (!row.parents || row.parents.length === 0) return "-";
                return row.parents.map((p) => p.name).join(", ");
            },
            {
                id: "parents",
                header: t("company.parentCompanies", "Parent Companies"),
                Cell: ({ row }) => {
                    const parents = row.original.parents;
                    if (!parents || parents.length === 0) {
                        return <GridText value="-" />;
                    }

                    const parentsText = parents.map((p) => p.name).join(", ");

                    return (
                        <Tooltip
                            title={parentsText}
                            arrow
                            placement="top"
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        color: "black",
                                        backgroundColor: "grey.300",
                                        "& .MuiTooltip-arrow": {
                                            color: "grey.300",
                                        },
                                    },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    cursor: "help",
                                }}
                            >
                                {parentsText}
                            </Box>
                        </Tooltip>
                    );
                },
            },
        ),
        columnHelper.accessor((row) => row.registrationNumber ?? "-", {
            id: "registrationNumber",
            header: t("company.registrationNumber", "Registration Number"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor((row) => row.clientId ?? "-", {
            id: "clientId",
            header: t("company.clientId", "Client ID"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor((row) => row.email ?? "-", {
            id: "email",
            header: t("company.email", "Email"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor((row) => row.phone ?? "-", {
            id: "phone",
            header: t("company.phone", "Phone"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor(
            (row) => {
                const address = [row.address1, row.city, row.country].filter(Boolean).join(", ");
                return address || "-";
            },
            {
                id: "address",
                header: t("company.address", "Address"),
                Cell: (value) => {
                    return <GridText value={value.renderedCellValue} />;
                },
            },
        ),
        columnHelper.accessor((row) => row.organizationSector?.name ?? "-", {
            id: "companySector_name", // More distinctive ID
            header: t("company.companySector", "Company Sector"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor((row) => row.organizationType?.name ?? "-", {
            id: "organizationType",
            header: t("company.organizationType", "Organization Type"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor((row) => row.isProvider || false, {
            id: "isProvider",
            header: t("company.isProvider", "Is Supplier"),
            Cell: (value) => {
                const isProvider = value.renderedCellValue;
                return (
                    <Chip
                        label={isProvider ? "Yes" : "No"}
                        color={isProvider ? "success" : "default"}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        }),
        columnHelper.accessor((row) => row.isPlatformOwner || false, {
            id: "isPlatformOwner",
            header: t("company.isPlatformOwner", "Platform Owner"),
            Cell: (value) => {
                const isOwner = value.renderedCellValue;
                return isOwner ?
                        <Chip label={t("company.platformOwner", "Owner")} color="primary" size="small" />
                    :   null;
            },
            enableSorting: false,
        }),
    ] as MRT_ColumnDef<CompanyGrid, string>[];

    // Add dynamic JSON attribute columns
    extendColumnsByJsonAttributes(mrt_columns, organizationJsonAttributes, data);

    // Add dynamic tag columns (hidden by default, can be enabled via column management)
    if (tagTypes && tagTypes.length > 0) {
        const tagColumns = generateTagColumns<CompanyGrid>({
            appearance: "legacy",
            tagTypes,
            tagValues,
            readonly: true, // Set to true for read-only columns displaying pills
        });

        mrt_columns.push(...tagColumns);
    }

    return mrt_columns;
};
